import { useCallback, useRef, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * useGridLayout — Universal AG Grid layout persistence hook.
 *
 * Persists column state (order, width, visibility, pinned), filter model,
 * and sort model to the server-side UserGridLayout table.
 *
 * USAGE:
 *   const gridLayout = useGridLayout('my-page-grid');
 *
 *   <AgGridReact
 *     onGridReady={gridLayout.onGridReady}
 *     ...
 *   />
 *
 *   <button onClick={gridLayout.saveLayout}>Save layout</button>
 *   <button onClick={gridLayout.resetToDefault}>Reset columns</button>
 *
 * The hook:
 *  - Loads saved layout from server on grid ready
 *  - Provides saveLayout() for explicit manual saves (with overwrite warning)
 *  - Does NOT auto-save on column changes — saves only happen via saveLayout()
 *  - Guards against save loops when applying saved state programmatically
 *  - Provides resetToDefault with confirmation dialog
 *  - Is completely self-contained — no external state management needed
 *
 * @param {string} gridKey - Unique identifier for this grid (e.g. 'order-entry-lines')
 * @param {object} [options]
 * @param {function} [options.onLayoutLoaded] - Callback after layout is applied (receives api)
 * @param {boolean} [options.persistFilters=false] - Whether to save/restore filter state
 * @param {boolean} [options.persistSort=false] - Whether to save/restore sort state
 * @returns {object} Grid layout handlers and utilities
 */
export default function useGridLayout(gridKey, options = {}) {
  const {
    onLayoutLoaded,
    persistFilters = false,
    persistSort = false,
  } = options;

  const apiRef = useRef(null);
  const layoutLoadedRef = useRef(false);
  const applyingLayoutRef = useRef(false);
  const mountedRef = useRef(true);
  const hasSavedLayoutRef = useRef(false);
  const [hasSavedLayout, setHasSavedLayout] = useState(false);

  // Cached saved state from the server, so we can (re)apply it once the grid's
  // columns actually exist. Grids that build columnDefs dynamically from fetched
  // data have NO columns at onGridReady time — applyColumnState then is a no-op.
  // We re-apply when AG Grid emits new columns (newColumnsLoaded/gridColumnsChanged).
  const pendingColumnStateRef = useRef(null);
  const pendingFilterStateRef = useRef(null);
  const pendingSortStateRef = useRef(null);
  // True once we've successfully applied the saved state to a non-empty column set,
  // so later column-definition updates don't clobber the user's in-session changes.
  const appliedToColumnsRef = useRef(false);
  const columnsListenerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      // Detach column-change listeners so we don't leak across remounts.
      if (columnsListenerRef.current?.api?.removeEventListener) {
        try {
          columnsListenerRef.current.api.removeEventListener('newColumnsLoaded', columnsListenerRef.current.handler);
          columnsListenerRef.current.api.removeEventListener('gridColumnsChanged', columnsListenerRef.current.handler);
          columnsListenerRef.current.api.removeEventListener('firstDataRendered', columnsListenerRef.current.handler);
        } catch { /* grid may already be destroyed */ }
      }
      columnsListenerRef.current = null;
    };
  }, []);

  /** Save current grid state to server (manual only — called from save button) */
  const saveLayout = useCallback(async () => {
    const api = apiRef.current;
    if (!api) return false;

    // Warn if overwriting an existing saved layout
    if (hasSavedLayoutRef.current) {
      const confirmed = window.confirm(
        'You already have a saved layout for this grid.\n\nOverwrite it with the current column arrangement?'
      );
      if (!confirmed) return false;
    }

    try {
      const columnState = api.getColumnState?.();
      const payload = { columnState };
      if (persistFilters) {
        payload.filterState = api.getFilterModel?.();
      }
      if (persistSort) {
        payload.sortState = api.getColumnState?.()
          ?.filter((c) => c.sort)
          .map((c) => ({ colId: c.colId, sort: c.sort, sortIndex: c.sortIndex }));
      }
      await axios.put(
        `/api/layouts/${encodeURIComponent(gridKey)}`,
        payload
      );
      hasSavedLayoutRef.current = true;
      if (mountedRef.current) setHasSavedLayout(true);
      return true;
    } catch (err) {
      console.warn(`[useGridLayout] Failed to save layout for "${gridKey}":`, err);
      return false;
    }
  }, [gridKey, persistFilters, persistSort]);

  /**
   * onColumnChanged / onColumnResized — kept for backward compatibility
   * but no longer auto-save. They are intentional no-ops.
   * Use saveLayout() explicitly via a save button.
   */
  const onColumnChanged = useCallback(() => {}, []);
  const onColumnResized = useCallback(() => {}, []);

  /**
   * Apply the cached saved state to the grid, but only when columns actually
   * exist. Returns true if applied. Used both on initial load and whenever the
   * grid's columns change (e.g. dynamic columns built from fetched data).
   */
  const applyPendingState = useCallback(
    (api) => {
      if (!api) return false;
      const columnState = pendingColumnStateRef.current;
      if (!Array.isArray(columnState) || !columnState.length) return false;
      const cols = api.getColumns?.();
      if (!cols || !cols.length) return false;

      applyingLayoutRef.current = true;
      try {
        api.applyColumnState?.({ state: columnState, applyOrder: true });
        if (persistFilters && pendingFilterStateRef.current && typeof pendingFilterStateRef.current === 'object') {
          api.setFilterModel?.(pendingFilterStateRef.current);
        }
        if (persistSort && Array.isArray(pendingSortStateRef.current) && pendingSortStateRef.current.length) {
          api.applyColumnState?.({
            state: pendingSortStateRef.current,
            defaultState: { sort: null },
          });
        }
      } finally {
        applyingLayoutRef.current = false;
      }

      // Only "lock" (stop re-applying on later column changes) once EVERY saved
      // column actually exists in the grid. Dynamic-column grids often have a
      // pinned action column present at grid-ready time before the data columns
      // load — locking on that partial set would miss the real layout, which was
      // exactly the bug on Sales Activity (pinned "Pick Review" column).
      const existing = new Set(
        cols.map((c) => (typeof c.getColId === 'function' ? c.getColId() : c.colId)).filter(Boolean)
      );
      const wanted = columnState.map((c) => c.colId).filter(Boolean);
      const allPresent = wanted.length > 0 && wanted.every((id) => existing.has(id));
      if (allPresent) appliedToColumnsRef.current = true;
      return true;
    },
    [persistFilters, persistSort]
  );

  /** Load layout from server and apply to grid */
  const loadLayout = useCallback(
    async (api) => {
      if (!api) return;
      applyingLayoutRef.current = true;
      try {
        const { data } = await axios.get(
          `/api/layouts/${encodeURIComponent(gridKey)}`
        );
        if (!mountedRef.current) return;

        let columnState = data?.columnState;
        let filterState = data?.filterState;
        let sortState = data?.sortState;

        // Parse JSON strings if needed
        if (typeof columnState === 'string') {
          try { columnState = JSON.parse(columnState); } catch { columnState = null; }
        }
        if (typeof filterState === 'string') {
          try { filterState = JSON.parse(filterState); } catch { filterState = null; }
        }
        if (typeof sortState === 'string') {
          try { sortState = JSON.parse(sortState); } catch { sortState = null; }
        }

        // Cache so we can (re)apply once columns exist (dynamic-column grids).
        pendingColumnStateRef.current = Array.isArray(columnState) ? columnState : null;
        pendingFilterStateRef.current = filterState && typeof filterState === 'object' ? filterState : null;
        pendingSortStateRef.current = Array.isArray(sortState) ? sortState : null;
        appliedToColumnsRef.current = false;

        // Apply now if the grid already has columns; otherwise the
        // newColumnsLoaded listener applies it once columns appear.
        applyingLayoutRef.current = false;
        applyPendingState(api);

        hasSavedLayoutRef.current = true;
        if (mountedRef.current) setHasSavedLayout(true);
        onLayoutLoaded?.(api);
      } catch (err) {
        // 404 = no saved layout, that's fine
        if (err?.response?.status !== 404) {
          console.warn(`[useGridLayout] Failed to load layout for "${gridKey}":`, err);
        }
        pendingColumnStateRef.current = null;
        pendingFilterStateRef.current = null;
        pendingSortStateRef.current = null;
        hasSavedLayoutRef.current = false;
        if (mountedRef.current) setHasSavedLayout(false);
      } finally {
        applyingLayoutRef.current = false;
        layoutLoadedRef.current = true;
      }
    },
    [gridKey, onLayoutLoaded, applyPendingState]
  );

  /**
   * Re-apply the cached saved layout when the grid's columns change. This is
   * what makes dynamic-column grids (columnDefs built from fetched data) persist
   * correctly: the columns don't exist at onGridReady, so we apply on first
   * appearance. Guarded so we never clobber the user's in-session changes once
   * the saved layout has already been applied to a real column set.
   */
  const handleColumnsChanged = useCallback(() => {
    if (applyingLayoutRef.current) return;
    if (appliedToColumnsRef.current) return;
    if (!pendingColumnStateRef.current) return;
    const api = apiRef.current;
    if (!api) return;
    applyPendingState(api);
  }, [applyPendingState]);

  /**
   * onGridReady handler — attach to <AgGridReact onGridReady={...}>
   * Stores the API reference and loads saved layout.
   */
  const onGridReady = useCallback(
    (params) => {
      apiRef.current = params.api;
      layoutLoadedRef.current = false;
      appliedToColumnsRef.current = false;

      // Listen for column-definition changes so dynamic-column grids get their
      // saved layout applied once the columns are created.
      if (columnsListenerRef.current?.api?.removeEventListener) {
        try {
          columnsListenerRef.current.api.removeEventListener('newColumnsLoaded', columnsListenerRef.current.handler);
          columnsListenerRef.current.api.removeEventListener('gridColumnsChanged', columnsListenerRef.current.handler);
          columnsListenerRef.current.api.removeEventListener('firstDataRendered', columnsListenerRef.current.handler);
        } catch { /* grid may already be destroyed */ }
      }
      params.api.addEventListener?.('newColumnsLoaded', handleColumnsChanged);
      params.api.addEventListener?.('gridColumnsChanged', handleColumnsChanged);
      params.api.addEventListener?.('firstDataRendered', handleColumnsChanged);
      columnsListenerRef.current = { api: params.api, handler: handleColumnsChanged };

      loadLayout(params.api);
    },
    [loadLayout, handleColumnsChanged]
  );

  /**
   * Reset columns to AG Grid defaults and delete the saved layout.
   * Shows a confirmation dialog first.
   * @returns {boolean} true if reset was performed, false if cancelled
   */
  const resetToDefault = useCallback(async () => {
    const api = apiRef.current;
    if (!api) return false;
    const confirmed = window.confirm(
      'Reset grid columns to default layout?\n\nThis will clear any column sizing, ordering, and visibility changes you\'ve made.'
    );
    if (!confirmed) return false;

    applyingLayoutRef.current = true;
    try {
      api.resetColumnState();
    } finally {
      applyingLayoutRef.current = false;
    }

    // Forget any cached saved layout so the columns-changed listener doesn't
    // re-apply the old state after a reset.
    pendingColumnStateRef.current = null;
    pendingFilterStateRef.current = null;
    pendingSortStateRef.current = null;
    appliedToColumnsRef.current = true;

    // Delete the saved layout from the server
    try {
      await axios.delete(`/api/layouts/${encodeURIComponent(gridKey)}`);
      hasSavedLayoutRef.current = false;
      if (mountedRef.current) setHasSavedLayout(false);
    } catch (err) {
      // 404 = nothing to delete, that's fine
      if (err?.response?.status !== 404) {
        console.warn(`[useGridLayout] Failed to delete layout for "${gridKey}":`, err);
      }
    }
    layoutLoadedRef.current = true;
    return true;
  }, [gridKey]);

  /**
   * Programmatically apply column state without triggering a save.
   * Use this when you need to change columns from code (e.g. toggling visibility).
   * @param {function} fn - Function that receives the grid API to make changes
   */
  const applyWithoutSaving = useCallback((fn) => {
    const api = apiRef.current;
    if (!api) return;
    applyingLayoutRef.current = true;
    try {
      fn(api);
    } finally {
      // Release the guard after a tick to let AG Grid finish its events
      setTimeout(() => {
        applyingLayoutRef.current = false;
      }, 50);
    }
  }, []);

  /**
   * Force a re-load of the saved layout from the server.
   * Useful when the grid is re-mounted (e.g. tab switch) but the hook persists.
   */
  const reloadLayout = useCallback(() => {
    const api = apiRef.current;
    if (!api) return;
    layoutLoadedRef.current = false;
    loadLayout(api);
  }, [loadLayout]);

  /**
   * Get whether the layout has been loaded from server.
   */
  const isLayoutLoaded = useCallback(() => layoutLoadedRef.current, []);

  /**
   * Get the current grid API ref (for advanced use cases).
   */
  const getApi = useCallback(() => apiRef.current, []);

  return {
    // AG Grid event handlers — wire these directly to <AgGridReact>
    onGridReady,
    onColumnChanged,
    onColumnResized,

    // Actions
    resetToDefault,
    reloadLayout,
    saveLayout,
    applyWithoutSaving,

    // State
    hasSavedLayout,

    // Utilities
    isLayoutLoaded,
    getApi,
  };
}
